import React from 'react';
import { MultiListbox, MultiListboxOption } from '@oysterjs/ui/multiselect';
import { RadioGroup, RadioField, Radio } from '@oysterjs/ui/radio';
import { Label } from '@oysterjs/ui/fieldset';
import {
  AgencyUserState,
  Visibility,
  useGetTeamsQuery,
  useGetAgencyUsersQuery
} from '~/api/graphql';
import { useTeamManagementEntitlement } from './use-visibility-entitlement';
import type { VisibilitySelectorValue } from './visibility-selector.utils';

const LEVEL_COPY: Record<Visibility, { title: string; description: string }> = {
  [Visibility.Private]: {
    title: 'Private',
    description: 'Only you'
  },
  [Visibility.Shared]: {
    title: 'Shared',
    description: 'Specific people and teams'
  },
  [Visibility.Public]: {
    title: 'Public',
    description: 'Everyone at your agency'
  }
};

interface VisibilitySelectorProps {
  value: VisibilitySelectorValue;
  onChange: (value: VisibilitySelectorValue) => void;
  /** When set, omits the PRIVATE option (admin disallow-private agencies). */
  disallowPrivate?: boolean;
  /** When set, omits the PUBLIC option (entities the server refuses to share agency-wide). */
  disallowPublic?: boolean;
  /** Users to leave out of the picker, e.g. a creator who already has access. */
  excludeUserIds?: string[];
}

/**
 * Three-segment level radio (`sky`-coloured to match the rest of the
 * codebase) plus MultiListbox pickers for users (always) and teams
 * (Enterprise-only) when SHARED is selected. The MultiListbox is the
 * established Acord-form-template pattern: clicking an option toggles its
 * checkmark without closing the dropdown so multiple selections happen in
 * one go.
 *
 * Plan-entitlement gating for the entire selector belongs to the caller:
 * use `useVisibilityEntitlement()` to decide whether to mount this component
 * at all. Below that gate, the selector trusts that the agency has at least
 * VISIBILITY_CONTROL.
 */
export const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({
  value,
  onChange,
  disallowPrivate = false,
  disallowPublic = false,
  excludeUserIds = []
}) => {
  const teamManagement = useTeamManagementEntitlement();
  const { data: usersData } = useGetAgencyUsersQuery({ fetchPolicy: 'cache-first' });
  const agencyUsers = (usersData?.agencyUsers ?? []).filter(
    (user) => user.state === AgencyUserState.Active && !excludeUserIds.includes(user.id)
  );

  const levels: Visibility[] = [Visibility.Private, Visibility.Shared, Visibility.Public].filter(
    (level) =>
      !(disallowPrivate && level === Visibility.Private) &&
      !(disallowPublic && level === Visibility.Public)
  );

  return (
    <div className="space-y-3">
      <RadioGroup
        value={value.level}
        onChange={(level: Visibility) => onChange({ ...value, level })}
        aria-label="Visibility"
        className="space-y-1"
      >
        {levels.map((level) => {
          const copy = LEVEL_COPY[level];
          return (
            <RadioField key={level}>
              <Radio color="sky" value={level} aria-label={copy.title} />
              <Label>
                <span className="text-sm font-medium text-neutral-900 dark:text-white">
                  {copy.title}
                </span>
                <span className="ml-1 text-xs text-neutral-500 dark:text-neutral-400">
                  — {copy.description}
                </span>
              </Label>
            </RadioField>
          );
        })}
      </RadioGroup>

      {value.level === Visibility.Shared ? (
        <div className="space-y-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
          <div>
            <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
              Share with users
            </span>
            <div className="mt-1">
              {agencyUsers.length === 0 && value.userIds.length === 0 ? (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  There is nobody else at your agency to share this with yet.
                </p>
              ) : (
                <MultiListbox
                  aria-label="Users to share with"
                  placeholder="Select users"
                  value={value.userIds}
                  onChange={(userIds: string[]) => onChange({ ...value, userIds })}
                >
                  {agencyUsers.map((u) => (
                    <MultiListboxOption key={u.id} value={u.id}>
                      {u.name || u.email}
                    </MultiListboxOption>
                  ))}
                </MultiListbox>
              )}
            </div>
          </div>

          {teamManagement ? (
            <TeamPicker
              teamIds={value.teamIds}
              onChange={(teamIds) => onChange({ ...value, teamIds })}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

interface TeamPickerProps {
  teamIds: string[];
  onChange: (teamIds: string[]) => void;
}

/**
 * Loads the agency's active teams via useGetTeamsQuery and renders one
 * MultiListboxOption per team. IDs already in the access list that are not
 * in the loaded set (e.g. archived teams) still render with a fallback
 * label so the editor can see and remove them — the UI never silently
 * drops a value the user can't act on.
 *
 * Wrapping the query in its own component keeps the team query out of the
 * network on Pro-tier agencies (which don't render this branch — the caller
 * checks useTeamManagementEntitlement before mounting).
 */
const TeamPicker: React.FC<TeamPickerProps> = ({ teamIds, onChange }) => {
  const { data } = useGetTeamsQuery({ fetchPolicy: 'cache-first' });
  const teams = data?.teams ?? [];
  const nameById = new Map(teams.map((t) => [t.id, t.name]));
  const staleIds = teamIds.filter((id) => !nameById.has(id));

  return (
    <div>
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
        Share with teams
      </span>
      <div className="mt-1">
        <MultiListbox
          aria-label="Teams to share with"
          placeholder="Select teams"
          value={teamIds}
          onChange={(next: string[]) => onChange(next)}
        >
          {teams.map((t) => (
            <MultiListboxOption key={t.id} value={t.id}>
              {t.name}
            </MultiListboxOption>
          ))}
          {staleIds.map((id) => (
            <MultiListboxOption key={id} value={id}>{`Unknown team (${id})`}</MultiListboxOption>
          ))}
        </MultiListbox>
      </div>
    </div>
  );
};
